<script lang="ts" setup>
import { XIcon, XButton, XSpinner, XAlert } from '@clicksign/excalibur';
import { useI18n } from '#i18n';
import { useClipboard } from '@vueuse/core';

import { BrandButton } from '~~base-ui';

import { sendPixFeedback } from '../../services';
import PixFeedbackModal from '../PixFeedbackModal/PixFeedbackModal.vue';
import PixHelpModal from '../PixHelpModal/PixHelpModal.vue';
import { PixCharge } from '../../schemas/pix';

export type PixConfirmationProps = {
  pixCharge: PixCharge
};

const { t } = useI18n();

const props = withDefaults(defineProps<PixConfirmationProps>(), {
  pixCharge: () => ({
    key: '',
    e2eId: '',
    amount: '',
    qrCodeText: '',
    qrCodeImage: '',
  }),
});

const isQRCodeClicked = ref(false);
const isFeedbackModalShown = ref(false);
const isHelpModalShown = ref(false);
const feedback = ref('');

const { copy } = useClipboard();

const isLoading = computed(() => !props.pixCharge?.qrCodeImage);

const copyQRCodeButtonIcon = computed(() => (isQRCodeClicked.value ? 'clone' : 'check'));

const copyQRCodeButtonText = computed(() => (isQRCodeClicked.value
  ? t('requirement.pix.sign.actions.copied')
  : t('requirement.pix.sign.actions.copy')));

function copyQRCodeURL() {
  isQRCodeClicked.value = true;
  if (props.pixCharge?.qrCodeText) copy(props.pixCharge.qrCodeText);
}

async function handleSendFeedback(event: string) {
  feedback.value = event;
  const payload = {
    feedback: {
      message: feedback.value,
    },
  };

  await sendPixFeedback(props.pixCharge.key, payload);
  isFeedbackModalShown.value = false;
  isHelpModalShown.value = true;
}
</script>

<template>
  <section
    :class="$style.pix"
    data-testid="substepPixConfirmation"
  >
    <header>
      <h3>
        {{ t('requirement.pix.sign.title') }}
      </h3>
      <p :class="$style.copyAndPasteText">
        <i18nT keypath="requirement.pix.sign.subtitle">
          <span>{{ t('requirement.pix.sign.pixCopyAndPaste') }}</span>
        </i18nT>
      </p>
      <p :class="$style.qrCodeText">
        <i18nT keypath="requirement.pix.sign.subtitle">
          <span>{{ t('requirement.pix.sign.qrCode') }}</span>
        </i18nT>
      </p>
    </header>

    <div :class="$style.qrCodeSection">
      <div :class="$style.copyAndPasteContainer">
        <p :class="$style.copyAndPasteAlternativeText">
          <i18nT keypath="requirement.pix.sign.alternativeSubtitle">
            <span>{{ t('requirement.pix.sign.pixCopyAndPaste') }}</span>
          </i18nT>
        </p>

        <BrandButton
          :class="$style.copyTextButton"
          :loading="isLoading"
          full-width
          data-testid="copyQRCodeButton"
          @click="copyQRCodeURL"
        >
          <XIcon
            v-if="!isLoading"
            left
            :icon="copyQRCodeButtonIcon"
            data-testid="copyQRCodeButtonIcon"
          />
          <span v-if="isLoading">{{ t('requirement.pix.sign.loading') }}</span>
          <span v-else>{{ copyQRCodeButtonText }}</span>
        </BrandButton>
      </div>

      <p :class="$style.qrCodeAlternativeText">
        <i18nT keypath="requirement.pix.sign.payWith">
          <span>{{ t('requirement.pix.sign.qrCode') }}</span>
        </i18nT>
      </p>

      <div>
        <div
          v-if="isLoading"
          :class="$style.qrCodeLoaderContainer"
          data-testid="spinner"
        >
          <XSpinner
            size="large"
            variant="dark"
          />
          <strong>{{ t('requirement.pix.sign.loading') }}</strong>
        </div>

        <img
          v-else
          :class="$style.qrCodeImage"
          :src="pixCharge.qrCodeImage || undefined"
          alt="QR Code"
        >
        <p :class="$style.pixValue">
          <i18nT keypath="requirement.pix.sign.cash">
            <span>{{ props.pixCharge?.amount || '0,01' }}</span>
          </i18nT>
        </p>
      </div>
    </div>

    <XAlert
      :class="$style.pixDisclaimer"
      type="warning"
      data-testid="pixConfirmationDisclaimer"
    >
      {{ t('requirement.pix.sign.disclaimer') }}
    </XAlert>

    <XButton
      :class="$style.helpButton"
      data-testid="feedbackHelpLink"
      design="link"
      @click="isFeedbackModalShown = true"
    >
      {{ t('requirement.pix.sign.help') }}
    </XButton>

    <PixFeedbackModal
      v-if="isFeedbackModalShown"
      data-testid="pixFeedbackModal"
      @close="isFeedbackModalShown = false"
      @send-feedback="handleSendFeedback"
    />

    <PixHelpModal
      v-if="isHelpModalShown"
      data-testid="pixHelpModal"
      @close="isHelpModalShown = false"
    />
  </section>
</template>

<style lang="scss" module>

.pix {
  display: flex;
  align-items: center;
  flex-direction: column;
}

.qrCodeText {
  display: none;

  @media (min-width: 992px) {
    display: block;
    text-align: center;
  }
}

.copyAndPasteText {
  display: block;
  text-align: center;

  @media (min-width: 992px) {
    display: none;
  }
}

.qrCodeAlternativeText {
  margin: var(--space-large) 0 var(--space-medium);

  @media (min-width: 992px) {
    display: none;
  }
}

.qrCodeSection {
  display: flex;
  align-items: center;
  flex-direction: column;
  width: 100%;

  .copyAndPasteAlternativeText {
    display: none;
  }

  @media (min-width: 992px) {
    flex-direction: column-reverse;
  }
}

.copyAndPasteContainer {
  display: flex;
  align-items: center;
  flex-direction: column;
  width: 100%;

  @media (min-width: 992px) {
    width: 288px;
  }
}

.copyTextButton {
  position: relative;
  margin-bottom: var(--space-large);
  text-align: center;
}

.qrCodeImage {
  width: 250px;
  height: 250px;
  padding: var(--space-small-xxx);
  border: var(--border-width-medium) solid;
  border-radius: var(--border-radius-medium);
}

.qrCodeLoaderContainer {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  width: 250px;
  height: 250px;
  gap: var(--space-small-xx);
  text-align: center;
  border: var(--border-width-medium) solid;
  border-radius: var(--border-radius-medium);
}

.alternativeText {
  display: none;
}

.pixValue {
  margin-top: var(--space-small-xx);
  margin-bottom: var(--space-regular);
  font-size: var(--font-size-large);
  font-weight: var(--font-weight-bold);
  text-align: center;
  line-height: var(--line-height-regular);

  @media (min-width: 992px) {
    margin-bottom: var(--space-medium);
  }
}

.pixDisclaimer {
  max-width: 288px;
  margin-bottom: var(--space-medium);
  text-align: center;
}

.helpButton {
  margin-bottom: var(--space-regular);
}

</style>
